import React from 'react';
import QuestionOrganism from '../QuestionOrganism';
import { ValidationRule } from '../types';
import { Field, Input } from 'components';
import { Trans } from 'react-i18next';
import {
  RTRIdentityQuestionComponentProps,
  StepNamesRTRIdentity,
} from 'questionFlow/flows/rightToRentIdentity/types';
import { useAnalyticsError, useAnalyticsValue } from 'monitoring/analyticsHooks';
import { useTranslatedValues } from 'hooks/useTranslatedValues';

const cmsLocation = '/questions/identity/shareCode';

const translations = {
  title: `${cmsLocation}.title`,
  subtitle: `${cmsLocation}.subtitle`,
  prompt: `${cmsLocation}.prompt`,
  placeholder: `${cmsLocation}.placeholder`,
  label: `${cmsLocation}.label`,
  helperText: `${cmsLocation}.helperText`,
} as const;

const name = StepNamesRTRIdentity.shareCode;

const ShareCodeQuestion = ({
  values,
  onSubmit,
  validationRules,
  ...props
}: RTRIdentityQuestionComponentProps) => {
  const [value, setValue] = useAnalyticsValue(name, values[name] || '');
  const [error, setError] = useAnalyticsError(name, '');
  const content = useTranslatedValues(translations);

  const validate = (v: string, rules: ValidationRule[]) => rules.find(({ rule }) => !rule(v));

  // Check if the form is valid (no validation errors and field is not empty)
  const isValid = value && !validate(value, validationRules[name]);

  const handleSubmit = () => {
    const result = validate(value, validationRules[name]);

    if (!result) {
      onSubmit({ key: name, value });
    } else {
      // Use the error message directly (now hardcoded in validation rules)
      setError(result.error);
    }
  };

  const handleOnChange = (e: React.ChangeEvent) => {
    const target = e.target as HTMLInputElement;

    if (error) {
      const result = validate(target.value, validationRules[name]);
      if (result) {
        // Use the error message directly (now hardcoded in validation rules)
        setError(result.error);
      } else {
        setError('');
      }
    }
    setValue(target.value);
  };

  const handleOnBlur = (e: React.FocusEvent) => {
    const target = e.target as HTMLInputElement;
    const result = validate(target.value, validationRules[name]);
    if (result) {
      // Use the error message directly (now hardcoded in validation rules)
      setError(result.error);
    } else {
      setError('');
    }
  };

  return (
    <QuestionOrganism
      id={name}
      data-testid={name}
      onSubmit={handleSubmit}
      title={content.title}
      subtitle={
        <Trans
          i18nKey={`${cmsLocation}.subtitle` as any}
          components={{
            br: <br></br>,
            p: <p />,
            a: (
              // eslint-disable-next-line jsx-a11y/anchor-has-content
              <a
                target="_blank"
                rel="noreferrer"
                href="https://www.gov.uk/prove-right-to-rent"
                className="text-blue-3"
              />
            ),
          }}
        />
      }
      prompt={content.prompt}
      disabled={!isValid}
      {...props}
    >
      <Field
        className="mb-4"
        data-testid="share-code"
        label={content.label as string}
        validationError={error}
        helperText={content.helperText as string}
      >
        <Input
          id={`${name}-share-code`}
          name="share-code"
          value={value}
          onChange={handleOnChange}
          aria-invalid={!!error}
          onBlur={handleOnBlur}
          placeholder={content.placeholder as string}
          autoComplete="off"
        />
      </Field>
    </QuestionOrganism>
  );
};

export default ShareCodeQuestion;
